import React, { useState, useRef, useEffect } from 'react';
import './LandingPage.css';
import FaqButton from './FaqButton';
import { useResponsive } from '../hooks/useResponsive';

function LandingPage({ onEmailSubmit }) {
  const { isMobile } = useResponsive();
  
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailInputRef = useRef(null);
  const firstNameInputRef = useRef(null);
  const buttonRef = useRef(null);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [isNewUser, setIsNewUser] = useState(true);
  const pinInputRef = useRef(null);
  const confirmPinInputRef = useRef(null);
  const [isCheckingUser, setIsCheckingUser] = useState(false);

  useEffect(() => {
    if (step === 1 && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [step]);

  useEffect(() => {
    if (step === 2 && firstNameInputRef.current) {
      firstNameInputRef.current.focus();
    }
  }, [step]);

  useEffect(() => {
    if (step === 3 && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [step]);

  const checkUserExists = async (email) => {
    if (!email) return false;
    setIsCheckingUser(true);

    try {
      const response = await fetch('/api/auth?action=check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });
      
      if (!response.ok) {
        throw new Error('Network error');
      }

      const data = await response.json();
      setIsNewUser(!data.exists);
      return data.exists;
    } catch (error) {
      console.error('Error checking user:', error);
      setError('Error checking user status');
      return false;
    } finally {
      setIsCheckingUser(false);
    }
  };

  const handleNextStep = async (e) => {
    e.preventDefault();
    setError('');

    if (step === 1) {
      // Email validation using regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Please enter a valid email address');
        return;
      }

      // Rest of the existing email validation logic...
      const userExists = await checkUserExists(email);
      if (userExists) {
        setIsNewUser(false);
        setStep(3); // Go directly to PIN entry for existing users
      } else {
        setIsNewUser(true);
        setStep(2); // Continue to name entry for new users
      }
    } else if (step === 2) {
      // Name validation...
      setStep(3);
    } else if (step === 3) {
      if (pin.length !== 4) {
        setError('PIN must be 4 digits');
        return;
      }
      if (isNewUser) {
        setStep(4); // Only go to confirm PIN for new users
      } else {
        setStep(5); // For existing users, go straight to final submit
      }
    } else if (step === 4) {
      // PIN confirmation only for new users
      if (pin !== confirmPin) {
        setError('PINs do not match');
        return;
      }
      setStep(5);
    }
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError('');
      
      // Double check user existence before proceeding
      const userExists = await checkUserExists(email);
      
      // If user exists but we thought they were new, or vice versa, stop and show error
      if (userExists === isNewUser) {
        setError(userExists ? 'User already exists' : 'User not found');
        return;
      }

      const response = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          pin: pin,
          firstName: isNewUser ? firstName.trim() : '',
          isNewUser: isNewUser
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Authentication failed');
      }

      onEmailSubmit({ 
        email: email.trim(), 
        firstName: data.firstName,
        pin: pin,
        isNewUser: isNewUser
      });

    } catch (error) {
      console.error('Submit error:', error);
      setError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setStep(1);
    }
  };

  const handleFinalKeyDown = (e) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleFinalSubmit(e);
    }
  };

  // Add responsive styles
  const getResponsiveStyles = () => ({
    container: {
      maxWidth: isMobile ? '100%' : '400px',
      padding: isMobile ? '0 20px' : '0',
    },
    title: {
      fontSize: isMobile ? '3rem' : '4rem',
    },
    description: {
      fontSize: isMobile ? '1rem' : '1.2rem',
      padding: isMobile ? '0 10px' : '0',
    }
  });

  // Get responsive styles
  const styles = getResponsiveStyles();

  return (
    <>
      <div className="background-shape shape-gradient"></div>
      <div className="container" style={styles.container}>
        <h1 style={styles.title}>feelr</h1>
        {step === 0 && (
          <div className="flex flex-col items-center gap-6 fade-in">
            <p className="text-text-color font-corben text-center max-w-[342px] px-5 min-h-[72px] flex items-center justify-center">
              Don't let your emotional life be diffuse. <br /> Get granular with feelr.
            </p>
            <form onSubmit={(e) => { e.preventDefault(); setStep(1); }} className="w-full max-w-[280px] px-5">
              <button
                onClick={() => setStep(1)}
                onKeyDown={handleKeyDown}
                className="w-full rounded-full border-0 py-2.5 px-4 bg-background-beige-bright text-text-color font-corben text-base hover:bg-white transition-all duration-300 hover:-translate-y-0.5 focus:outline-none"
                tabIndex={0}
                autoFocus
              >
                Give demo a try
              </button>
            </form>
          </div>
        )}
        {step === 1 && (
          <form onSubmit={handleNextStep} className="flex flex-col items-center gap-6" noValidate>
            <p className="text-text-color font-corben text-center max-w-[342px] px-5 min-h-[72px] flex items-center justify-center">
              Welcome!<br />
              Before we start, what's your email?
            </p>
            
            <div className="relative w-full max-w-[280px] px-5">
              <div className="relative flex rounded-full shadow-sm">
                <input
                  ref={emailInputRef}
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting || isCheckingUser}
                  required
                  className={`block w-full rounded-l-full border-0 py-2.5 pl-4 pr-3 
                    bg-background-beige-bright text-text-color font-corben text-base 
                    ring-0 placeholder:text-placeholder-color/70 
                    focus:outline-none focus:ring-0 focus:bg-white 
                    transition-all duration-300
                    ${error ? 'border-error ring-1 ring-error' : ''}
                  `}
                  autoFocus
                />
                <button 
                  type="submit"
                  className={`relative -ml-px inline-flex items-center gap-x-1.5 
                    rounded-r-full px-3 py-2 bg-background-beige-bright text-text-color 
                    hover:bg-white disabled:opacity-70 disabled:cursor-not-allowed
                    ${error ? 'border-error ring-1 ring-error' : ''}
                  `}
                  disabled={isSubmitting || isCheckingUser || !email}
                >
                  {isCheckingUser ? (
                    <div className="w-5 h-5 border-2 border-text-color border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg 
                      viewBox="0 0 24 24" 
                      className="w-6 h-6 fill-current transition-transform duration-300 hover:translate-x-0.5"
                    >
                      <path d="M8 4 L20 12 L8 20 Z" />
                    </svg>
                  )}
                </button>
              </div>
              {error && (
                <p className="absolute -bottom-6 left-0 right-0 px-5 text-sm text-error font-corben">
                  {error}
                </p>
              )}
            </div>
          </form>
        )}
        {step === 2 && (
          <form onSubmit={handleNextStep} className="flex flex-col items-center gap-6" noValidate>
            <p className="text-text-color font-corben text-center max-w-[342px] px-5 min-h-[72px] flex items-center justify-center">
              And what should we call you?
            </p>
            
            <div className="w-full max-w-[280px] px-5">
              <div className="relative flex rounded-full shadow-sm">
                <input
                  ref={firstNameInputRef}
                  type="text"
                  placeholder="Your first name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isSubmitting}
                  required
                  className={`block w-full rounded-l-full border-0 py-2.5 pl-4 pr-3 
                    bg-background-beige-bright text-text-color font-corben text-base 
                    ring-0 placeholder:text-placeholder-color/70 
                    focus:outline-none focus:ring-0 focus:bg-white 
                    transition-all duration-300
                    ${error ? 'border-error ring-1 ring-error' : ''}
                  `}
                  autoFocus
                />
                <button 
                  type="submit"
                  className={`relative -ml-px inline-flex items-center gap-x-1.5 
                    rounded-r-full px-3 py-2 bg-background-beige-bright text-text-color 
                    hover:bg-white disabled:opacity-70 disabled:cursor-not-allowed
                    ${error ? 'border-error ring-1 ring-error' : ''}
                  `}
                  disabled={isSubmitting || !firstName}
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-text-color border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg 
                      viewBox="0 0 24 24" 
                      className="w-6 h-6 fill-current transition-transform duration-300 hover:translate-x-0.5"
                    >
                      <path d="M8 4 L20 12 L8 20 Z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
        {step === 3 && (
          <form onSubmit={handleNextStep} className="flex flex-col items-center gap-6" noValidate>
            <p className="text-text-color font-corben text-center max-w-[342px] px-5 min-h-[72px] flex items-center justify-center">
              {isNewUser ? 'And a 4-digit PIN\nto protect your account?' : 'Please enter your 4-digit PIN\nto access your account.'}
            </p>
            
            <div className="relative w-full max-w-[280px] px-5">
              <div className="relative flex rounded-full shadow-sm">
                <input
                  ref={pinInputRef}
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="4"
                  placeholder="0000"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.slice(0, 4))}
                  disabled={isSubmitting}
                  required
                  className={`block w-full rounded-l-full border-0 py-2.5 pl-4 pr-3 
                    bg-background-beige-bright text-text-color font-corben text-base 
                    ring-0 placeholder:text-placeholder-color/70 
                    focus:outline-none focus:ring-0 focus:bg-white 
                    transition-all duration-300
                    ${error ? 'border-error ring-1 ring-error' : ''}
                  `}
                  autoComplete="new-password"
                  autoFocus
                />
                <button 
                  type="submit"
                  className={`relative -ml-px inline-flex items-center gap-x-1.5 
                    rounded-r-full px-3 py-2 bg-background-beige-bright text-text-color 
                    hover:bg-white disabled:opacity-70 disabled:cursor-not-allowed
                    ${error ? 'border-error ring-1 ring-error' : ''}
                  `}
                  disabled={isSubmitting || !pin}
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-text-color border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg 
                      viewBox="0 0 24 24" 
                      className="w-6 h-6 fill-current transition-transform duration-300 hover:translate-x-0.5"
                    >
                      <path d="M8 4 L20 12 L8 20 Z" />
                    </svg>
                  )}
                </button>
              </div>
              {error && (
                <p className="absolute -bottom-6 left-0 right-0 px-5 text-sm text-error font-corben">
                  {error}
                </p>
              )}
            </div>
          </form>
        )}

        {step === 4 && (
          <form onSubmit={handleNextStep} className="flex flex-col items-center gap-6" noValidate>
            <p className="text-text-color font-corben text-center max-w-[342px] px-5 min-h-[72px] flex items-center justify-center">
              Almost there! <br /> Please re-enter your PIN to confirm.
            </p>
            
            <div className="relative w-full max-w-[280px] px-5">
              <div className="relative flex rounded-full shadow-sm">
                <input
                  ref={confirmPinInputRef}
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="4"
                  placeholder="0000"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.slice(0, 4))}
                  disabled={isSubmitting}
                  required
                  className={`block w-full rounded-l-full border-0 py-2.5 pl-4 pr-3 
                    bg-background-beige-bright text-text-color font-corben text-base 
                    ring-0 placeholder:text-placeholder-color/70 
                    focus:outline-none focus:ring-0 focus:bg-white 
                    transition-all duration-300
                    ${error ? 'border-error ring-1 ring-error' : ''}
                  `}
                  autoComplete="new-password"
                  autoFocus
                />
                <button 
                  type="submit"
                  className={`relative -ml-px inline-flex items-center gap-x-1.5 
                    rounded-r-full px-3 py-2 bg-background-beige-bright text-text-color 
                    hover:bg-white disabled:opacity-70 disabled:cursor-not-allowed
                    ${error ? 'border-error ring-1 ring-error' : ''}
                  `}
                  disabled={isSubmitting || !confirmPin}
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-text-color border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg 
                      viewBox="0 0 24 24" 
                      className="w-6 h-6 fill-current transition-transform duration-300 hover:translate-x-0.5"
                    >
                      <path d="M8 4 L20 12 L8 20 Z" />
                    </svg>
                  )}
                </button>
              </div>
              {error && (
                <p className="absolute -bottom-6 left-0 right-0 px-5 text-sm text-error font-corben">
                  {error}
                </p>
              )}
            </div>
          </form>
        )}
        {step === 5 && (
          <form onSubmit={handleFinalSubmit} className="flex flex-col items-center gap-6" noValidate>
            <p className="text-text-color font-corben text-center max-w-[342px] px-5 min-h-[72px] flex items-center justify-center">
              Ready to chat?<br />Click the button below to start.
            </p>
            
            <div className="relative w-full max-w-[280px] px-5">
              <button
                onClick={handleFinalSubmit}
                onKeyDown={handleFinalKeyDown}
                className={`w-full rounded-full border-0 py-2.5 px-4 
                  bg-background-beige-bright text-text-color font-corben text-base 
                  hover:bg-white transition-all duration-300 
                  hover:-translate-y-0.5 disabled:opacity-70 
                  disabled:cursor-not-allowed disabled:hover:translate-y-0 
                  focus:outline-none
                  ${error ? 'border-error ring-1 ring-error' : ''}
                `}
                disabled={isSubmitting}
                autoFocus
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-text-color border-t-transparent rounded-full animate-spin mx-auto" />
                ) : (
                  'Let\'s get granular!'
                )}
              </button>
              {error && (
                <p className="absolute -bottom-6 left-0 right-0 px-5 text-sm text-error font-corben">
                  {error}
                </p>
              )}
            </div>
          </form>
        )}
      </div>
      <div className="disclaimer">
        <span>
          feelr is a wellbeing solution designed to support emotional literacy and is not intended to replace professional care. If you are facing mental health challenges, please consult your doctor or healthcare provider.
        </span>
      </div>
      <FaqButton />
    </>
  );
}

export default LandingPage;
